import { useNavigate } from "react-router-dom";
import { extractUserEnumEditLink } from "../../components/entitiesReferences/InternalEntityLink";
import { userEnumService } from "../../services/userEnumService";
import { SystemEntityCollections } from "../../shared/enums/shared-enums";
import { UserEnum } from "../../types/userEnum";
import { useEntityQueryClient } from "./entityQueryClient";
import { enqueueSnackbar, SnackbarAction } from "notistack";
import { Button } from "@mui/material";
import { useQuery } from "@tanstack/react-query";

export const useFetchUserEnums = (names: string[]) => {
  const query = useQuery<Record<string, string[]>, Error>({
    queryKey: [
      SystemEntityCollections.USER_ENUM,
      "userEnumByName",
      ...names.map((name) => `user-enum-${name}`),
    ],
    enabled: names.length > 0,
    queryFn: async () => {
      const response = await userEnumService.fetchAll({
        bodyParams: { name: names },
      });

      const returnedEnums: Record<string, { values: string[] }> = {};
      for (const item of response.data) {
        returnedEnums[item.name] = { values: item.values };
      }

      const result: Record<string, string[]> = {};
      const missing: string[] = [];

      for (const name of names) {
        if (returnedEnums[name]) {
          result[name] = returnedEnums[name].values;
        } else {
          missing.push(name);
        }
      }

      if (missing.length > 0) {
        throw new Error(`Missing user enums: ${missing.join(", ")}`);
      }
      return result;
    },
  });

  const errorMessage = query.isError
    ? `Failed to process fetch User Enums.`
    : null;

  if (query.isError) {
    console.error(`Error fetching User Enums:`, query.error);
    enqueueSnackbar(errorMessage, { variant: "error" });
  }

  return {
    ...query,
    errorMessage,
  };
};

export const useFetchSubExistenceMap = (name: string) => {
  const hasEnumName = Boolean(name);

  const query = useQuery<Record<string, boolean>, Error>({
    queryKey: [
      SystemEntityCollections.USER_ENUM,
      "subExistenceMap",
      `user-enum-${name}`,
    ],
    enabled: hasEnumName,
    queryFn: async () => {
      const response = await userEnumService.fetchSubExistenceMap(name);
      return response;
    },
  });

  const errorMessage = query.isError
    ? `Failed to process fetch User Enums.`
    : null;

  if (query.isError) {
    console.error(`Error fetching User Enums:`, query.error);
    enqueueSnackbar(errorMessage, { variant: "error" });
  }

  return {
    ...query,
    errorMessage,
  };
};

const useUserEnumQuery = () => {
  const navigate = useNavigate();
  return useEntityQueryClient<UserEnum>({
    service: userEnumService,
    collection: SystemEntityCollections.USER_ENUM,
    snackbarDisplayName: (data: UserEnum) => {
      return data.name || "User Enum";
    },
    snackbarAction: (data: UserEnum): SnackbarAction => (
      <Button
        variant="text"
        onClick={() => navigate(extractUserEnumEditLink(data))}
        sx={{ color: "white" }}
      >
        View {data.name || "User Enum"}
      </Button>
    ),
  });
};

export default useUserEnumQuery;
